import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Grid, Button, Divider, Card, TextField } from '@mui/material';
import { FETCH_CART, GET_SHOPIFY_LISTING } from '../../graphql/queries';
import { useMutation, useQuery } from '@apollo/client';
import { ShopifyImage } from '../../graphql/generated';
import { ADD_TO_CART, CREATE_CART } from '../../graphql/mutations';

const ListingDetailsPage = () => {
  const { listingId } = useParams(); // Retrieve product ID from URL
  const location = useLocation();
  const navigate = useNavigate();
  const { shop_domain } = location.state || {}; // Extract shop_domain from state

  const { loading, error, data } = useQuery(GET_SHOPIFY_LISTING, {
    variables: { shop_domain, id: listingId },
    skip: !shop_domain, // Skip the query if shop_domain is not available
  });

  const [selectedQuantity, setSelectedQuantity] = useState(1);

  const handleQuantityChange = (event: any) => {
    const value = Number(event.target.value);
    if (value >= 1 && value <= selectedVariant?.inventoryQuantity) setSelectedQuantity(value); // Prevent negative values or zero
    if (value > selectedVariant?.inventoryQuantity) setSelectedQuantity(selectedVariant?.inventoryQuantity)
  };

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null);
  const [disabledOptions, setDisabledOptions] = useState<Record<string, Set<string>>>({});


  const [addToCart] = useMutation(ADD_TO_CART);
  const [createCart] = useMutation(CREATE_CART);

  console.log("Cart ID:", localStorage.getItem('shopifyCartId') || 'No cart ID in local storage')

  const handleAddToCart = async () => {
    const cartId = localStorage.getItem('shopifyCartId'); // Check for existing cartId in local storage

    try {
      if (cartId) {
        console.log("adding to existing cart")
        // Add to existing cart
        const response = await addToCart({
          variables: {
            cartId,
            items: [
              {
                merchandiseId: `${selectedVariant.id}`,
                quantity: selectedQuantity,
              },
            ],
          },
          refetchQueries: [FETCH_CART]
        });

        const updatedCart = response.data.addToCart;
        console.log('Updated Cart:', updatedCart);
      } else {
        // Create a new cart
        const response = await createCart({
          variables: {
            items: [
              {
                merchandiseId: `${selectedVariant.id}`,
                quantity: selectedQuantity,
              },
            ],
          },
        });

        console.log("RESPONSE", response)

        const newCart = response.data.createCart;
        console.log('New Cart Created:', newCart);

        // Save the new cart ID to local storage
        localStorage.setItem('shopifyCartId', newCart.id);
      }
    } catch (error: any) {
      console.error('Error adding to cart:', error.message);
      alert('Failed to add item to cart. Please try again.');
    }
  };

  const handleCheckout = async () => {
    await handleAddToCart();
    navigate("/cart")
  }


  // Update selectedOptions and selectedVariant when the product loads
  useEffect(() => {
    if (data?.getShopifyListing) {
      const product = data.getShopifyListing;

      // Initialize selectedOptions to the first value of each option
      const initialSelectedOptions: Record<string, string> = {};
      product.options.forEach((option: any) => {
        if (option.values.length > 0) {
          initialSelectedOptions[option.name] = option.values[0];
        }
      });

      setSelectedOptions(initialSelectedOptions);
      updateSelectedVariant(product.variants, initialSelectedOptions);
    }
  }, [data]);

  // Function to update the selectedVariant based on selectedOptions
  const updateSelectedVariant = (variants: any[], selectedOptions: Record<string, string>) => {
    const matchedVariant = variants.find((variant: any) =>
      variant.selectedOptions.every(
        (option: any) => selectedOptions[option.name] === option.value
      )
    );
    console.log("Variant selected", matchedVariant)
    setSelectedVariant(matchedVariant || null);
  };

  // Function to update disabled options based on inventory and selectedOptions
  const updateDisabledOptions = (
    variants: any[],
    newlySelectedOption: Record<string, string>,
    options: any[]
  ) => {
    const newDisabledOptions: Record<string, Set<string>> = {};
    console.log("selected options", selectedOptions)

    options.forEach((option: any) => {
      console.log("option", option)
      const currentOptionName = option.name;

      // Skip the current row (already selected)
      if (newlySelectedOption[currentOptionName]) {
        console.log("here", newlySelectedOption[currentOptionName])
        return;
      }

      const disabledValues = new Set<string>();

      // Iterate over the values in this option
      option.values.forEach((value: string) => {
        console.log("Option value:", value)
        // Check if this value would match any variant with inventory > 0
        const isValueAvailable = variants.some((variant: any) => {
          return (
            variant.inventoryQuantity > 0 &&
            variant.selectedOptions.every((variantOption: any) => {
              // Match selected options or this option value
              return (
                (variantOption.name === currentOptionName && variantOption.value === value) ||
                (selectedOptions[variantOption.name] === variantOption.value)
              );
            })
          );
        });

        if (!isValueAvailable) {
          disabledValues.add(value);
        }
      });

      newDisabledOptions[currentOptionName] = disabledValues;
    });

    setDisabledOptions(newDisabledOptions);
  };

  // Handle option selection
  const handleOptionSelect = (optionName: string, value: string) => {
    const updatedOptions = { ...selectedOptions, [optionName]: value };
    setSelectedOptions(updatedOptions);

    // Update selectedVariant based on the new options
    if (data?.getShopifyListing?.variants) {
      updateSelectedVariant(data.getShopifyListing.variants, updatedOptions);
      updateDisabledOptions(
        data.getShopifyListing.variants,
        {[optionName]: value},
        data.getShopifyListing.options
      );
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography>Error: {error.message}</Typography>;
  }

  const product = data.getShopifyListing;

  if (!product) {
    return <Typography>No product details available.</Typography>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        padding: 3,
        backgroundColor: '#fff',
        margin: '20px auto',
        borderRadius: '10px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
      }}
    >
      {/* Product Images Section */}
      <Grid container spacing={2}>
        {/* Image Stack */}
        <Grid item xs={2}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              border: '1px solid #ddd',
              borderRadius: 2,
              width: 124,
              overflow: 'hidden',
              justifyContent: 'center',
            }}
          >
            {product.images.map((image: ShopifyImage, index: number) => (
              <Box
                key={index}
                sx={{
                  cursor: 'pointer',
                  border: selectedImage === image.src ? '2px solid #1976d2' : 'none',
                  borderRadius: '4px',
                  width: 120,
                  height: 180,
                  justifyContent: 'center',
                  alignContent: 'center',
                  overflow: 'hidden',
                }}
                onClick={() => setSelectedImage(image.src)}
              >
                <img
                  src={image.src}
                  alt={image.altText || 'Product Thumbnail'}
                  width="100%"
                  style={{ borderRadius: '4px' }}
                />
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Large Displayed Image */}
        <Grid item xs={5}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              borderRadius: '4px',
              border: '1px solid #ddd',
            }}
          >
            <img
              src={selectedImage || product.images[0]?.src}
              alt="Selected Product"
              style={{ maxHeight: '400px', maxWidth: '100%', borderRadius: '4px' }}
            />
          </Box>
        </Grid>

        {/* Product Details Card */}
      <Grid item xs={5}>
        <Card sx={{ padding: 3 }}>
          {/* Category Information */}
          <Typography variant="body2" color="textSecondary">
            {product.product_type || 'Category'} / {product.vendor || 'Brand'}
          </Typography>

          {/* Product Title */}
          <Typography variant="h5" sx={{ marginTop: 1 }}>
            {product.title}
          </Typography>

          {/* Price */}
          <Typography variant="h6" sx={{ marginTop: 1, marginBottom: 2 }}>
            ${selectedVariant ? selectedVariant.price : 'N/A'}
          </Typography>

          <Divider />

          {/* Product Options */}
          {product.options.map((option: any) => (
            <Box key={option.id} sx={{ marginTop: 2 }}>
              <Typography variant="body1" sx={{ marginBottom: 1 }}>
                {option.name}:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {option.values.map((value: string, valueIndex: number) => (
                  <Button
                    key={`${option.id}-${valueIndex}`}
                    variant={selectedOptions[option.name] === value ? 'contained' : 'outlined'}
                    disabled={
                      disabledOptions[option.name]?.has(value) // Disable if unavailable
                    }
                    onClick={() => handleOptionSelect(option.name, value)}
                    sx={{ textTransform: 'none' }}
                  >
                    {value}
                  </Button>
                ))}
              </Box>
            </Box>
          ))}

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              marginTop: 3,
              flexDirection: "column",
            }}
          >
            
            <Typography variant="body1" sx={{ marginBottom: 1 }} alignSelf={"start"} textAlign={"start"}>
              {selectedVariant?.inventoryQuantity || ""} available
            </Typography>
             {/* Quantity Selector */}
            <TextField
              label="Quantity"
              type="number"
              variant="outlined"
              size="small"
              value={selectedQuantity}
              onChange={handleQuantityChange}
              inputProps={{ min: 1, max: selectedVariant?.inventoryQuantity || 1 }} // Prevents negative numbers
              sx={{ width: "100px" }} // Adjust width as needed
            />

            <Box
            sx={{
              display: 'flex',
              flexDirection: "row",
            }}>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={!selectedVariant} // Disable if no valid variant
              onClick={handleAddToCart}
            >
              Add to Bag
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              disabled={!selectedVariant} // Disable if no valid variant
              onClick={handleCheckout}
            >
              Checkout
            </Button>
            </Box>
          </Box>
        </Card>
      </Grid>
      </Grid>
    </Box>
  );
};

export default ListingDetailsPage;
